import { fromPairs, get, isEmpty, isNil } from "lodash";
import { CONTACT_POINT_SYSTEM, ORGANIZATION_TYPES } from "utils/constants";
import { envVarKeysOf } from "utils/helpers/integrations";

export const STEP_KEYS = {
  GENERAL: "GENERAL",
  ADMIN: "ADMIN",
  KEY: "KEY",
  INTEGRATIONS: "INTEGRATIONS",
  API: "API",
  PLANS: "PLANS",
  DOCUMENTS: "DOCUMENTS",
};

export const STEPS = [
  STEP_KEYS.GENERAL,
  STEP_KEYS.KEY,
  STEP_KEYS.INTEGRATIONS,
  STEP_KEYS.API,
  STEP_KEYS.PLANS,
  STEP_KEYS.DOCUMENTS,
];

export const STEP_INDEX = fromPairs(STEPS.map((key, index) => [key, index]));

export const CATEGORIES = [ORGANIZATION_TYPES.INS, ORGANIZATION_TYPES.BROKER];

export const INTEGRATION_CATEGORIES = [
  "SIGNATURE",
  "EMAIL",
  "PAYMENT",
  "SMS",
  "GESTION",
  "DOCUMENTS",
  "STORAGE",
  "AUTH",
  "TRACKING",
];

const DEFAULT_CONNECTORS = {
  SIGNATURE: "signature.yousign",
  EMAIL: "email.brevo",
};

export const LATER_FIELDS = ["siretNumber", "form"];

export const TELECOM_SYSTEMS = [
  CONTACT_POINT_SYSTEM.PHONE,
  CONTACT_POINT_SYSTEM.EMAIL,
  CONTACT_POINT_SYSTEM.ADDRESS,
  CONTACT_POINT_SYSTEM.ZIP_CODE,
  CONTACT_POINT_SYSTEM.CITY,
];

export const INITIAL_VALUES = {
  organization: {
    name: "",
    type: ORGANIZATION_TYPES.INS,
    form: "",
    siretNumber: "",
  },
  telecoms: {},
  icon: null,
  user: {
    gender: "",
    firstname: "",
    lastname: "",
    phone: "",
    email: "",
  },
  integrations: {},
};

export const envKeys = (connector) =>
  envVarKeysOf(get(connector, "configKeys"));

export const isSecretKey = (key) => /KEY|SECRET|TOKEN|PASSWORD/.test(key);

const providersOf = (catalog, category) =>
  catalog.filter((connector) => connector.category === category);

export const integrationOf = (values, catalog, category) => {
  const state = get(values, ["integrations", category]) || {};
  const providers = providersOf(catalog, category);
  const connectorKey =
    state.connector ||
    DEFAULT_CONNECTORS[category] ||
    get(providers, "0.key", null);
  const connector =
    providers.find(({ key }) => key === connectorKey) ||
    get(providers, "0", null);
  const keys = envKeys(connector);
  const entered = get(state, ["values", get(connector, "key")]) || {};
  const present = get(state, ["present", get(connector, "key")]) || [];
  return {
    category,
    on: Boolean(state.on),
    state,
    providers,
    connector,
    keys,
    entered,
    present,
    filled: keys.filter(
      (key) => !isEmpty(entered[key]) || present.includes(key),
    ).length,
  };
};

export const integrationsState = (catalog) =>
  fromPairs(
    INTEGRATION_CATEGORIES.map((category) => {
      const providers = providersOf(catalog, category);
      const chosen = providers.find(
        ({ enabled, selected, presentConfigKeys }) =>
          enabled && (selected || !isEmpty(presentConfigKeys)),
      );
      return [
        category,
        {
          on: !isNil(chosen),
          connector: get(chosen, "key", null),
          present: fromPairs(
            providers.map(({ key, presentConfigKeys }) => [
              key,
              presentConfigKeys || [],
            ]),
          ),
        },
      ];
    }),
  );

export const laterFields = (values) =>
  LATER_FIELDS.filter((field) => isEmpty(get(values, ["organization", field])));

export const integrationsChanges = (values, initialValues, catalog) => {
  const payload = { values: {}, enabled: {} };
  for (const category of INTEGRATION_CATEGORIES) {
    const before = integrationOf(initialValues, catalog, category);
    const after = integrationOf(values, catalog, category);
    const chosen = get(after, "connector.key");
    const switched =
      after.on && (!before.on || get(before, "connector.key") !== chosen);
    if (switched)
      for (const provider of after.providers)
        payload.enabled[provider.key] = provider.key === chosen;
    if (before.on && !after.on)
      for (const provider of after.providers)
        payload.enabled[provider.key] = false;
    for (const key of after.on ? after.keys : [])
      if (!isEmpty(after.entered[key]))
        payload.values[key] = after.entered[key];
  }
  return payload;
};

export const hasIntegrationChanges = (changes) =>
  !isEmpty(changes.values) || !isEmpty(changes.enabled);

export const telecomsPayload = (telecoms, newId) =>
  TELECOM_SYSTEMS.filter((system) => !isEmpty(get(telecoms, system))).map(
    (system) => ({ id: newId(), system, value: telecoms[system] }),
  );
